import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm, FieldPath } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useErrorHandle } from '../common/error/errorHandler'
import { LotteryReserveEntry, selectLotteryReserveEntry, setLotteryReserve } from '../common/store/slices/lotteryReserve'
import { FacilityInfoType, GoingTimeType } from '../common/lotteryReservation'
import { executeGetChild } from '../../dataAccess/webApi/dao/childrenDao'
import { GetChildDto } from '../../dataAccess/webApi/dto/childrenDto'
import { toApiYmd, toApiHms, differenceInHoursMinutes, formatYmd } from '../../utils/dateUtil'
import { dateToNumber, undefinedPropsToOptional } from '../../utils/objectUtil'
import { ReactBatchUpdater } from '../../utils/reactUtil'
import { facilityLotteryReservationConfirmationUrl } from '../common/constant/appUrl'
import { availabilityStatus, yesNo, Flag, mailType, lotteryMethod } from '../common/constant/classification'
import { getUseReason } from '../common/useReason'
import { showLoading } from '../common/store/slices/application'
import { getUser } from '../common/user'
import { useOperationLog } from '../common/operationLog'
import { OperationId } from '../common/constant/operationLog'
import { isAfter } from 'date-fns'
import { getSendingFlagMailSetting } from '../common/getFacilityMailSetting'
import { GOING_DATETIME_DISPLAY_METHODS, INPUT_FIELD_USAGE, MEDICAL_DOC_USAGE, ATTACHED_DOC_USAGE } from '../common/constant/projectMst'
import { LotteryReservationSettingDecodeMst } from '../common/lotteryReservation'
import { 
  setStringifyNamesChildInfoList, 
  getLotteryReservationDecodeMstValue, 
  ChildLotteryReservationInfoType, 
  ShowMessage, 
  SelectAndOtherType,
  stringifySelectBox
} from '../common/lotteryReservation'
import { getProjectMstDataToRefer } from '../common/projectMst'
import { GetProjectDetailDto } from '../../dataAccess/webApi/dto/projectsDto'
import { translate } from '../../i18n'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { getFacility } from '../common/facility'

export interface Inputs {
  lunchFlag: string
  snackFlag: string
  postponeCancelWaitFlag: string
  goingToFacilityTime: Date
  goingTimes: GoingTimeType[]
  note: string
  showMessages: ShowMessage[]
  children: ChildLotteryReservationInfoType[]
  consentCheck: string
  preferenceProvisionCertificateFlag: Flag | null
  formUseReason?: SelectAndOtherType
}

export type facilityLotteryReservationInputKey = FieldPath<Inputs>

interface PageState {
  // 送信フラグデータ
  cancelSendingFlag: boolean
  // 表示メッセージ
  showMessages: ShowMessage[]
  // デコードマスタ
  decodeMst: LotteryReservationSettingDecodeMst
}

interface ChildData extends GetChildDto {
  childId: string
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const reserveEntry = useSelector(selectLotteryReserveEntry)
  const entryInput = reserveEntry?.commonInput
  const { addOperationLog } = useOperationLog()
  const sysCtrl = useSelector(selectSystemControl)

  const [state, setState] = useState<PageState>()

  const formMethods = useForm<Inputs>()

  const history = useHistory()
  
  const { fields: goingTimeFileds } = useFieldArray({
    control: formMethods.control,
    name: 'goingTimes',
  })
  
  const { fields: childrenWatch } = useFieldArray({
    control: formMethods.control,
    name: 'children',
  })
  
  const lotteryReservationReference = useMemo(() => {
    if (reserveEntry?.projectMst && reserveEntry?.facility && reserveEntry?.parentInfo && reserveEntry?.useReason && reserveEntry?.childInfoList) {
      const { projectMst, facility, parentInfo, useReasonCategory, useReasonDetailCategory, useReasonDetailDescription, useReason, childInfoList, isUseReasonCategoryHidden } = reserveEntry
      return {
        facilityName: facility.facilityName,
        useReasonCategory: useReasonCategory,
        useReasonDetailCategory: useReasonDetailCategory,
        useReasonDetailDescription: useReasonDetailDescription,
        childInfoList,
        parentInfo,
        projectMst,
        useReason: {...useReason},
        isUseReasonCategoryHidden,
        isReservationCancelWait: false,
      }
    }
  }, [reserveEntry])

  const onSubmit = useCallback(
    (data: Inputs) => {
      if (!state) return;

      addOperationLog({ operationId: OperationId.OP_00000025 })
      let isError = false

      // TODO: readonlyを回避するため
      const goingTimes = data.goingTimes.map((goingTime) => ({
        ...goingTime,
      }))
      
      goingTimes.forEach((goingTime, index) => {
        const goingToFaclityDatetime = new Date(`${goingTime.goingToFacilityDate} ${goingTime.goingToFacilityTime}`)
        const goingHomeDatetime = new Date(`${goingTime.goingHomeDate} ${goingTime.goingHomeTime}`)
        if (isAfter(goingToFaclityDatetime, goingHomeDatetime)) {
          formMethods.setError(`goingTimes.${index}.goingToFacilityTime`, {
            message: translate("facilityReservationForm.form.error.goingTimes"),
          })
          isError = true
        }
        if (reserveEntry?.projectMst && reserveEntry.projectMst.goingDatetimeLimit !== null) {
          const difference = differenceInHoursMinutes(goingHomeDatetime, goingToFaclityDatetime);
          const diffHours = differenceInHoursMinutes(goingHomeDatetime, goingToFaclityDatetime).hour + (difference.minute === 0 ? 0 : 1)
          if (diffHours >  reserveEntry.projectMst.goingDatetimeLimit) {
            formMethods.setError(`goingTimes.${index}.goingHomeTime`, {
              message: translate('facilityReservationForm.form.error.goingDatetimesLimit', reserveEntry.projectMst.goingDatetimeLimit.toString())
            })
            formMethods.setError(`goingTimes.${index}.goingToFacilityTime`, {})
            isError = true            
          }
        }
      })

      const children:ChildLotteryReservationInfoType[] = JSON.parse(JSON.stringify(data.children));

      if (isError) return

      dispatch(
        reserveEntry &&
        setLotteryReserve({
          ...reserveEntry,
          childInfoList: (state?.decodeMst) ? setStringifyNamesChildInfoList(children, state.decodeMst) : children,
          commonInput: {
            ...reserveEntry.commonInput,
            lunchFlag: data.lunchFlag,
            snackFlag: data.snackFlag,
            postponeCancelWaitFlag: data.postponeCancelWaitFlag,
            citizenNote: data.note,
            goingTimes,
            consentCheck: data.consentCheck,
            preferenceProvisionCertificateFlag: data.preferenceProvisionCertificateFlag,
            formUseReason: {
              select: null,
              other: null,
              ...data.formUseReason,
              stringify: (state.decodeMst) 
                  ? stringifySelectBox(
                    state.decodeMst.formUseReason, 
                    data.formUseReason?.select, 
                    data.formUseReason?.other,
                    ["その他"]
                  ): undefined
            },
          },
        })
      )
      history.push(facilityLotteryReservationConfirmationUrl.url()) //値の送り先を設定
    },
    [reserveEntry, addOperationLog, formMethods, state]
  )

  // 初期化
  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })
    dispatch(
      showLoading(
        errorHandle(async () => {
          if ( !reserveEntry ) {
            // 前画面入力情報が無い場合はエラーにするので何もしない
            return
          }

          // 予約希望日ないで最も古い日付における事業マスタを参照
          const usageDates = reserveEntry.usageDatetimes.map(( usageDatetime ) => new Date(usageDatetime.usageDate) )
          usageDates.sort((a, b) => a.getTime() - b.getTime());

          // 保護者・お子様情報の取得
          const [facility, user, children, useReason, projectMst, decodeMst] = await Promise.all([
            getFacility(reserveEntry.facility.facilityId, usageDates[0]),
            getUser(),
            getChildren(... reserveEntry.childInfoList.map((childInfo) => childInfo.childId)),
            reserveEntry.useReason ? null : getUseReason(),
            getProjectMstDataToRefer(reserveEntry.facility.facilityId, usageDates),
            getLotteryReservationDecodeMstValue()
          ])
          
          // 給食注文
          let lunchFlag: string = yesNo.no
          if (reserveEntry.facility.lunchAcceptFlag === yesNo.yes) {
            lunchFlag = entryInput?.lunchFlag ?? reserveEntry.facility.lunchInitialSelect ?? yesNo.yes
          }
          // おやつ注文
          let snackFlag: string = yesNo.no
          if (reserveEntry.facility.snackAcceptFlag === yesNo.yes) {
            snackFlag = entryInput?.snackFlag ?? reserveEntry.facility.snackInitialSelect ?? yesNo.yes
          }

          // 登降園予定時間
          const goingTimes: GoingTimeType[] = []
          for (let index = 0; index < reserveEntry.usageDatetimes.length; index++) {
            const usageDatetime = reserveEntry.usageDatetimes[index];

            if ( projectMst.goingDatetimeDisplayMethods === GOING_DATETIME_DISPLAY_METHODS.USED_ALL_FACILITY ) {
              const prevGoingTime = reserveEntry.commonInput?.goingTimes[index];
              const goingToFaclityDatetime = new Date(prevGoingTime ? `${prevGoingTime.goingToFacilityDate} ${prevGoingTime.goingToFacilityTime}` : usageDatetime.useFromDatetime)
              const goingHomeDatetime = new Date(prevGoingTime ? `${prevGoingTime.goingHomeDate} ${prevGoingTime.goingHomeTime}` : usageDatetime.useToDatetime)
              const goingToFacilityDate = toApiYmd(new Date(goingToFaclityDatetime))
              const goingToFacilityTime = toApiHms(new Date(goingToFaclityDatetime))
              const goingHomeDate = toApiYmd(new Date(goingHomeDatetime))
              const goingHomeTime = toApiHms(new Date(goingHomeDatetime))
              goingTimes.push({
                goingToFacilityDate,
                goingToFacilityTime,
                goingHomeDate,
                goingHomeTime,
              })
            } else {
              goingTimes.push({
                goingToFacilityDate: '',
                goingToFacilityTime: '',
                goingHomeDate: '',
                goingHomeTime: '',
              })
            }
          }

          // メール設定（送信フラグ）情報を取得し、送信フラグ："0"(無)の場合、FALSEとする
          let cancelSendingFlag = true
          if (reserveEntry?.usageDatetimes[0].status === availabilityStatus.wait) {
            const sendingFlagInfo = await getSendingFlagMailSetting({mailId: mailType.immediatelyCancelVacant, facilityId: reserveEntry.facility.facilityId})
            if (sendingFlagInfo && sendingFlagInfo.sendingFlag === '0') {
              cancelSendingFlag = false
            }
          }

          // 予約が取れなかった場合項目
          let postponeCancelWaitFlag: string = yesNo.no
          if (isShownPostponeCancelWaitItem(reserveEntry.facility)) {
            if (entryInput) {
              postponeCancelWaitFlag = entryInput.postponeCancelWaitFlag
            } else {
              postponeCancelWaitFlag = facility.cancelWaitInitialSelect
            }
          }

          // 備考
          const detailSetting = reserveEntry.facility.detailSetting.length === 1 
                                ? reserveEntry.facility.detailSetting[0] 
                                : reserveEntry.facility.detailSetting.find((item) => item.useReasonCode === reserveEntry.useReasonCategory);
          let note = detailSetting?.citizenNoteEntryTemplate ?? '';
          if (entryInput) {
            note = entryInput.citizenNote ?? '';
          }

          let formUseReason: SelectAndOtherType | undefined = undefined
          // 選択項目は「不要」の場合はundefinedで登録を行う
          if ( projectMst.useReasonInput !== INPUT_FIELD_USAGE.NO_USE ) {
            formUseReason = {
              select: null,
              other: null,
              ... entryInput?.formUseReason
            }
          }
          
          if (projectMst.medicalDocUsage === MEDICAL_DOC_USAGE.BY_FACILITY) {
            projectMst.medicalDocUsage = reserveEntry.facility.medicalDoc ?? projectMst.medicalDocUsage
          }

          // 領収書兼提供証明書の発行希望
          const preferenceProvisionCertificateFlag = entryInput?.preferenceProvisionCertificateFlag ?? null
          
          const newReserveEntry = {
            ... reserveEntry,
            // 施設情報
            facility: {
              ... facility,
              facilityId: reserveEntry.facility.facilityId
            },
            // 保護者情報
            parentInfo: {
              name: user.name,
              kana: user.kana,
              tel: user.tel,
              email: user.email,
              residenceCategory: user.residenceCategory,
            }
          }

          // お子様情報のセット
          const childInfoList = setChildInfo(children, newReserveEntry, projectMst);

          const batchUpdater = new ReactBatchUpdater()
          batchUpdater.add(() =>
            formMethods.reset({
              ...formMethods.getValues(),
              ...undefinedPropsToOptional({
                purposeOfUse: reserveEntry?.useReasonCategory,
                lunchFlag,
                snackFlag,
                goingTimes,
                postponeCancelWaitFlag,
                formUseReason: {
                  ... formUseReason,
                  ... (
                    formUseReason?.other === null && {
                      other: ''
                    }
                  )
                },
                note,
                showMessages: [],
                children: childInfoList,
                consentCheck: entryInput?.consentCheck,
                preferenceProvisionCertificateFlag,
              }),
            })
          )
          batchUpdater.add(() =>
            dispatch(
              setLotteryReserve({
                ...newReserveEntry,
                // お子さま情報
                childInfoList,
                ... (
                  useReason && {
                    useReason
                  }
                ),
                projectMst
              })
            )
          )
          batchUpdater.addSetter(setState, { 
            cancelSendingFlag,
            showMessages: [],
            decodeMst
          })
          batchUpdater.execute()
        })
      )
    )
  }, [])

  return {
    reserveEntry,
    lotteryReservationReference,
    isShownPostponeCancelWaitItem: isShownPostponeCancelWaitItem(reserveEntry?.facility),
    formMethods,
    onSubmit,
    goingTimeFileds,
    childrenWatch,
    showSiblingsLotteryResultReferenceFlag: sysCtrl.lotteryMethod === lotteryMethod.byParent,
    ... state
  }
}

//------------------------------------------------------------------------------------------
const isShownPostponeCancelWaitItem = (facility: FacilityInfoType | undefined) => {
  if (facility) {
    return facility.immediatelyReservationFlag === yesNo.no && facility.postponeCancelWaitAcceptFlag === yesNo.yes
  } else {
    return false
  }
}

async function getChildren(...childIds: string[]) {
  const childResponse = await Promise.all(
    childIds.map(async (childId) => {
      const childData = await executeGetChild(childId);
      return { childId, ...childData };
    })
  )

  return childResponse.map(response => {
    const childWithUndefinedProps = response.result
    return { ...childWithUndefinedProps, childId: response.childId }
  })
}

const setChildInfo = (childrenData: ChildData[], reserveEntry: LotteryReserveEntry, projectMst: GetProjectDetailDto): ChildLotteryReservationInfoType[] => {
  return childrenData.map((child) => {
    const prevChildInfo = reserveEntry.childInfoList.find((childInfo) => childInfo.childId === child.childId);    
    const returnChildInfo: ChildLotteryReservationInfoType = {
      childId: child.childId,
      childInfo: {
        name: child.name,
        kana: child.kana,
        birthday: dateToNumber(child.birthday),
        gender: child.gender,
        allergyFlag: child.allergyFlag,
        brothersSistersFlag: child.brothersSistersFlag,
      },
      febrileSeizures: null,
      medicine: {
        rx: null,
        otc: null,
      },
      foodAllergyFlag: null,
    }
    
    // 医師連絡票
    if ( projectMst.medicalDocUsage !== MEDICAL_DOC_USAGE.NO_USE ) {
      returnChildInfo.medicalDoc = {
        docs:  prevChildInfo?.medicalDoc?.docs ?? [],
        hasSubmitted: prevChildInfo?.medicalDoc?.hasSubmitted ?? Flag.OFF,
      }
      if (projectMst.medicalDocUsage === MEDICAL_DOC_USAGE.MUST_HOSPITAL_INFO) {
        const date = prevChildInfo?.hospitalInfo?.date ? formatYmd(new Date(prevChildInfo?.hospitalInfo?.date as string)) : null
        returnChildInfo.hospitalInfo = {
          date: prevChildInfo?.hospitalInfo?.date ? new Date(prevChildInfo?.hospitalInfo?.date).toISOString() : null,
          time: prevChildInfo?.hospitalInfo?.time 
          ? toApiHms(new Date(`${date} ${prevChildInfo?.hospitalInfo?.time}`)) 
          : null,
          hospitalName: prevChildInfo?.hospitalInfo?.hospitalName ?? null,
        }
      }
    }

    // 症状
    if ( projectMst.symptomsInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.symptom = {
        symptoms: [],
        other: '',
        ... prevChildInfo?.symptom
      }
    }
    // 病名
    if ( projectMst.diagonosisInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.diagonosis = {
        first: {
          select: null,
          other: ''
        },
        second: {
          select: null,
          other: ''
        },
        ... prevChildInfo?.diagonosis
      }
    }
    // 流行り病
    if ( projectMst.epidemicInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.epidemic = {
        select: '',
        other: '',
        ... prevChildInfo?.epidemic
      }
    }

    // 体温
    if ( projectMst.bodyTemperatureInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.temperature = {
        integer: undefined,
        fractional: undefined,
        ... prevChildInfo?.temperature
      }
    }

    // ラジオボタンの初期値設定
    // 別ページから遷移してきて、入力情報を持っている場合はその値を用いる
    // そうでない場合は、任意入力の場合のみ OFF を設定し、それ以外は null を設定する
    // 服用薬
    returnChildInfo.medicine.rx = prevChildInfo?.medicine.rx ?? (projectMst.medicineInput === INPUT_FIELD_USAGE.OPTIONAL ? Flag.OFF : null)
    returnChildInfo.medicine.otc = prevChildInfo?.medicine.otc ?? (projectMst.medicineInput === INPUT_FIELD_USAGE.OPTIONAL ? Flag.OFF : null)
    // 熱けいれん
    returnChildInfo.febrileSeizures = prevChildInfo?.febrileSeizures ?? (projectMst.febrileSeizuresInput === INPUT_FIELD_USAGE.OPTIONAL ? Flag.OFF : null)
    // 食物アレルギー
    returnChildInfo.foodAllergyFlag = prevChildInfo?.foodAllergyFlag ?? (projectMst.allergyInput === INPUT_FIELD_USAGE.OPTIONAL ? Flag.OFF : null)

    // 容態変化の対応
    if ( projectMst.emergencyActionInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.emergencyAction = {
        action: {
          select: null
        },
        note: '',
        ... prevChildInfo?.emergencyAction
      }
    }
    // 身長
    if ( projectMst.heightInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.height = {
        integer: undefined,
        fractional: undefined,
        ... prevChildInfo?.height
      }
    }
    // 体重
    if ( projectMst.weightInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.weight = {
        integer: undefined,
        fractional: undefined,
        ... prevChildInfo?.weight
      }
    }
    // 栄養方法
    if ( projectMst.eatingMethodInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.eatingMethod = {
        method: {
          select: null
        },
        detail: {
          select: null
        },
        milkAmount: {
          integer: undefined,
          fractional: undefined,
        },
        milkFrequency: undefined,
        ... prevChildInfo?.eatingMethod
      }
    }
    // 食事状況
    if ( projectMst.eatingAbilityInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.eatingAbility = {
        select: prevChildInfo?.eatingAbility?.select ?? null
      }
    }
    // 行動
    if ( projectMst.actionInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.actions = {
        action: {
          select: null
        },
        sleepMethod: '',
        ... prevChildInfo?.actions
      }
    }
    // 排便
    if ( projectMst.toiletAbilityInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.toiletAbility = {
        select: prevChildInfo?.toiletAbility?.select ?? null
      }
    }
    // 配慮が必要な事項
    if ( projectMst.considerationsInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.considerations = prevChildInfo?.considerations ?? ''
    }
    // 減免
    if ( projectMst.reductionInput !== INPUT_FIELD_USAGE.NO_USE ) {
      returnChildInfo.reductionType = {
        reduction: {
          select: prevChildInfo?.reductionType?.reduction.select ?? null
        },
        consentCheck: prevChildInfo?.reductionType?.consentCheck ?? undefined
      }
    }

    // 添付書類
    if ( projectMst.attachedDocInput !== ATTACHED_DOC_USAGE.NO_USE ) {
      returnChildInfo.attachedDoc = {
        docs: prevChildInfo?.attachedDoc?.docs ?? []
      }
    }

    // 施設別予約時項目
    if (reserveEntry.facility.facilityInputFieldsDisplayFlag === Flag.ON) {
      reserveEntry.facility.facilityInputFields?.forEach(element => {
        const key = element.id;
        returnChildInfo.facilityInputFields = {
          ...returnChildInfo.facilityInputFields ?? {}
        }

        if (prevChildInfo?.facilityInputFields && prevChildInfo?.facilityInputFields[key]) {
          returnChildInfo.facilityInputFields[key] = {
            ...prevChildInfo?.facilityInputFields[key],
            order: element.order,
          };
        } else {
          returnChildInfo.facilityInputFields[key] = {
            id: null,
            answer: "",
            updateDatetime: null,
            facilityReservationInputFieldId: key,
            order: element.order,
          };
        }
      });
    }

    returnChildInfo.siblingsLotteryResultReferenceFlag = prevChildInfo?.siblingsLotteryResultReferenceFlag ?? Flag.OFF
    
    return returnChildInfo;
  })
}
